/**
 * Admin → ML Execution Targets tab (M1-B3 frontend).
 *
 * CRUD-lite UI for ml_execution_targets — the cross-account wiring that lets the
 * platform run time-series ML jobs INSIDE the customer's AWS account (Bar B:
 * source data never leaves the account). Admins add a target (account id + role
 * ARN + external id), then click Test Connection (assume-role + trivial
 * SageMaker probe). external_id is masked on display; the role ARN + external id
 * are the ONLY auth — no long-lived AWS keys are ever entered here.
 */
import React, { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { Button, Badge, Card, Input, Select, Modal } from "../../components/ui";
import { mlApi } from "../../api/client";

const STATUS_COLOR = {
  active: "#16a34a",
  disabled: COLORS.textMuted || "#6b7280",
  error: "#ef4444",
};

// FCOV: the list endpoint returns either a bare array of targets or a
// {data:[...]} envelope. Normalize to a plain array. Extracted + exported so
// the branch is unit-testable; wired into refresh() below.
export function normalizeTargets(rows) {
  return Array.isArray(rows) ? rows : rows?.data || [];
}

export default function MLExecutionTargetsTab() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [testResult, setTestResult] = useState(null); // { id, ok, stage, message }
  const [testingId, setTestingId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await mlApi.listExecutionTargets();
      setTargets(normalizeTargets(rows));
    } catch (e) {
      setError(e?.message || "Failed to load execution targets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runTest = async (row) => {
    setTestingId(row.id);
    setTestResult(null);
    try {
      const result = await mlApi.testExecutionTarget(row.id);
      setTestResult({ id: row.id, ...result });
    } catch (e) {
      setTestResult({ id: row.id, ok: false, stage: "request", message: e?.message || "Test failed" });
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div style={{ padding: "4px 2px" }} data-testid="ml-execution-targets-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: COLORS.text }}>ML Execution Targets</h3>
          <p style={{ margin: "4px 0 0", color: COLORS.textMuted, fontSize: 13, maxWidth: 640 }}>
            Run time-series ML jobs inside your own AWS account. The platform assumes a
            cross-account role you provide — your source data never leaves your account.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={refresh}>Refresh</Button>
          <Button onClick={() => setShowCreate(true)} data-testid="ml-add-target-btn">Add Target</Button>
        </div>
      </div>

      {error && (
        <Card style={{ borderColor: "#ef4444", marginBottom: 12 }}>
          <span style={{ color: "#ef4444" }}>{error}</span>
        </Card>
      )}

      {loading ? (
        <Card><span style={{ color: COLORS.textMuted }}>Loading…</span></Card>
      ) : targets.length === 0 ? (
        <Card>
          <div style={{ textAlign: "center", padding: "24px 0", color: COLORS.textMuted }} data-testid="ml-empty-state">
            No execution targets yet. Click <strong>Add Target</strong> to connect your AWS account.
            <div style={{ marginTop: 8, fontSize: 12 }}>
              Deploy the CloudFormation / Terraform template first, then paste the role ARN here.
            </div>
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {targets.map((t) => (
            <Card key={t.id} style={{ padding: "16px 18px" }} data-testid="ml-target-row">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, color: COLORS.text }}>{t.aws_account_id}</span>
                    <Badge color={t.connector_type === "sagemaker" ? "#2563eb" : "#7c3aed"}>{t.connector_type}</Badge>
                    <Badge color={STATUS_COLOR[t.status] || COLORS.textMuted}>{t.status}</Badge>
                  </div>
                  <div style={{ marginTop: 6, fontFamily: "monospace", fontSize: 12, color: COLORS.textMuted, overflowWrap: "anywhere" }}>
                    {t.cross_account_role_arn}
                  </div>
                  <div style={{ marginTop: 2, fontSize: 12, color: COLORS.textMuted }}>
                    external id: <span style={{ fontFamily: "monospace" }}>{t.external_id}</span>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => runTest(t)}
                  disabled={testingId === t.id}
                  data-testid="ml-test-btn"
                >
                  {testingId === t.id ? "Testing…" : "Test Connection"}
                </Button>
              </div>

              {testResult && testResult.id === t.id && (
                <div
                  data-testid="ml-test-result"
                  style={{
                    marginTop: 12, padding: "8px 12px", borderRadius: 8, fontSize: 13,
                    background: testResult.ok ? "rgba(22,163,74,0.10)" : "rgba(239,68,68,0.10)",
                    color: testResult.ok ? "#16a34a" : "#ef4444",
                    border: `1px solid ${testResult.ok ? "#16a34a" : "#ef4444"}`,
                  }}
                >
                  {testResult.ok ? "✓ " : "✗ "}
                  <strong>{testResult.ok ? "Connected" : "Failed"}</strong>
                  {testResult.stage ? ` (${testResult.stage})` : ""} — {testResult.message}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateTargetModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
    </div>
  );
}

function CreateTargetModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    aws_account_id: "",
    cross_account_role_arn: "",
    external_id: "",
    connector_type: "sagemaker",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await mlApi.createExecutionTarget({
        aws_account_id: form.aws_account_id.trim(),
        cross_account_role_arn: form.cross_account_role_arn.trim(),
        external_id: form.external_id,
        connector_type: form.connector_type,
        scoped_config: {},
      });
      onCreated();
    } catch (e) {
      // Backend 400 carries a validation message (bad account id / role ARN / etc.)
      setErr(e?.message || "Failed to create target");
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add ML Execution Target"
      width={520}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="ml-create-submit">
            {submitting ? "Adding…" : "Add Target"}
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12, color: COLORS.textMuted }}>
          Deploy the onboarding template (CloudFormation / Terraform) in your account first,
          then paste the <strong>OrchestrationRoleArn</strong> output and the same external id.
          No AWS keys are stored — only the role ARN + external id.
        </p>
        <Select label="Connector" value={form.connector_type} onChange={set("connector_type")} data-testid="ml-connector-type">
          <option value="sagemaker">SageMaker (in customer account)</option>
          <option value="in_db">In-database (reserved)</option>
        </Select>
        <Input
          label="AWS Account ID"
          placeholder="123456789012"
          value={form.aws_account_id}
          onChange={set("aws_account_id")}
          data-testid="ml-account-id"
        />
        <Input
          label="Cross-account Role ARN"
          placeholder="arn:aws:iam::123456789012:role/i2-ml-orchestration-role"
          value={form.cross_account_role_arn}
          onChange={set("cross_account_role_arn")}
          data-testid="ml-role-arn"
        />
        <Input
          label="External ID"
          type="password"
          placeholder="shared secret from the template (≥16 chars)"
          value={form.external_id}
          onChange={set("external_id")}
          data-testid="ml-external-id"
        />
        {err && (
          <div style={{ color: "#ef4444", fontSize: 13 }} data-testid="ml-create-error">{err}</div>
        )}
      </div>
    </Modal>
  );
}
